import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Calendar, MessageCircle, Linkedin, X} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

const Contact: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      value: "mmarikennedy@gmail.com",
      action: "mailto:mmarikennedy@gmail.com"
    },
    {
      icon: Phone,
      title: "Phone",
      value: "+255 719 579 733",
      action: "tel:+255719579733"
    },
    {
      icon: MapPin,
      title: "Location",
      value: "Dar es Salaam, Tanzania",
      action: null
    }
  ];

  const quickActions = [
    {
      icon: Calendar,
      title: "Schedule Meeting",
      description: "Book a strategic consultation",
      action: "#"
    },
    {
      icon: MessageCircle,
      title: "Quick Message",
      description: "Send an instant message",
      action: "#"
    }
  ];

  return (
    <section id="contact" className="py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className={`text-4xl md:text-5xl font-light mb-6 ${
            isDark ? 'text-white' : 'text-charcoal'
          }`}>
            Let's <span className="gradient-text font-medium">Connect</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-sunrise-orange to-serengeti-gold mx-auto rounded-full mb-4"></div>
          <p className={`text-lg max-w-2xl mx-auto ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Ready to discuss opportunities, partnerships, or strategic initiatives?
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div className="animate-slide-in-left">
            <div className={`glass border rounded-3xl p-8 lg:p-12 shadow-2xl ${
              isDark ? 'glass-dark' : 'glass-light'
            }`}>
              <h3 className={`text-2xl font-medium mb-8 ${
                isDark ? 'text-white' : 'text-charcoal'
              }`}>
                Get In Touch
              </h3>
              
              <p className={`text-lg leading-relaxed mb-8 ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                I'm always interested in connecting with fellow communications professionals, entrepreneurs, and organizations. 
                Whether you're looking for strategic communications consulting, PR services, or digital transformation support, 
                I'd love to hear from you and discuss how we can work together.
              </p>

              {/* Contact Details */}
              <div className="space-y-6 mb-8">
                {contactInfo.map((info) => (
                  <div 
                    key={info.title}
                    className={`flex items-center gap-4 p-4 rounded-2xl transition-all duration-300 hover:scale-105 ${
                      isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50/50 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`p-3 rounded-xl ${
                      isDark ? 'bg-sunrise-orange/20' : 'bg-sunrise-orange/10'
                    }`}>
                      <info.icon size={24} className="text-sunrise-orange" />
                    </div>
                    <div>
                      <h4 className={`font-medium ${isDark ? 'text-white' : 'text-charcoal'}`}>
                        {info.title}
                      </h4>
                      {info.action ? (
                        <a 
                          href={info.action}
                          className={`text-sm transition-colors duration-300 hover:text-sunrise-orange ${
                            isDark ? 'text-gray-400' : 'text-gray-600'
                          }`}
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {info.value}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="mb-8">
                <h4 className={`text-lg font-medium mb-4 ${
                  isDark ? 'text-white' : 'text-charcoal'
                }`}>
                  Quick Actions
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  {quickActions.map((action) => (
                    <button
                      key={action.title}
                      className={`p-4 rounded-2xl text-left transition-all duration-300 hover:scale-105 ${
                        isDark 
                          ? 'bg-white/5 hover:bg-white/10 border border-white/10' 
                          : 'bg-gray-50/50 hover:bg-gray-50 border border-gray-200/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <action.icon size={20} className="text-sunrise-orange" />
                        <div>
                          <h5 className={`font-medium ${isDark ? 'text-white' : 'text-charcoal'}`}>
                            {action.title}
                          </h5>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {action.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h4 className={`text-lg font-medium mb-4 ${
                  isDark ? 'text-white' : 'text-charcoal'
                }`}>
                  Connect on Social
                </h4>
                <div className="flex gap-4">
                  <a 
                    href="https://www.linkedin.com/in/kennedymmari"
                    className={`p-3 rounded-full glass border transition-all duration-300 hover:scale-110 ${
                      isDark 
                        ? 'glass-dark text-white hover:bg-white/10 border-white/20' 
                        : 'glass-light text-charcoal hover:bg-white/60 border-gray-300/50'
                    }`}
                  >
                    <Linkedin size={20} />
                  </a>
                  <a 
                    href="#"
                    className={`p-3 rounded-full glass border transition-all duration-300 hover:scale-110 ${
                      isDark 
                        ? 'glass-dark text-white hover:bg-white/10 border-white/20' 
                        : 'glass-light text-charcoal hover:bg-white/60 border-gray-300/50'
                    }`}
                  >
                    <X size={20} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="animate-slide-in-right">
            <div className={`glass border rounded-3xl p-8 lg:p-12 shadow-2xl ${
              isDark ? 'glass-dark' : 'glass-light'
            }`}>
              <h3 className={`text-2xl font-medium mb-8 ${
                isDark ? 'text-white' : 'text-charcoal'
              }`}>
                Send a Message
              </h3>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-sunrise-orange/50 ${
                        isDark 
                          ? 'bg-white/5 border-white/20 text-white placeholder-gray-400' 
                          : 'bg-white/50 border-gray-300/50 text-charcoal placeholder-gray-500'
                      }`}
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-sunrise-orange/50 ${
                        isDark 
                          ? 'bg-white/5 border-white/20 text-white placeholder-gray-400' 
                          : 'bg-white/50 border-gray-300/50 text-charcoal placeholder-gray-500'
                      }`}
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-sunrise-orange/50 ${
                      isDark 
                        ? 'bg-white/5 border-white/20 text-white placeholder-gray-400' 
                        : 'bg-white/50 border-gray-300/50 text-charcoal placeholder-gray-500'
                    }`}
                    placeholder="Your company name"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-sunrise-orange/50 ${
                      isDark 
                        ? 'bg-white/5 border-white/20 text-white placeholder-gray-400' 
                        : 'bg-white/50 border-gray-300/50 text-charcoal placeholder-gray-500'
                    }`}
                    placeholder="What's this about?"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-sunrise-orange/50 resize-none ${
                      isDark 
                        ? 'bg-white/5 border-white/20 text-white placeholder-gray-400' 
                        : 'bg-white/50 border-gray-300/50 text-charcoal placeholder-gray-500'
                    }`}
                    placeholder="Tell me about your project or opportunity..."
                  />
                </div>

                <button
                  type="submit"
                  className={`w-full glass border px-8 py-4 rounded-full transition-all duration-300 flex items-center justify-center gap-3 font-medium hover:scale-105 ${
                    isDark 
                      ? 'glass-dark text-white hover:bg-white/10 border-white/20' 
                      : 'glass-light text-charcoal hover:bg-white/60 border-gray-300/50'
                  }`}
                >
                  Send Message
                  <Send size={20} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
